import React, { useState } from 'react';

import { Upload, FileCheck } from 'lucide-react';

interface FileUploadProps {
  id: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  title?: string;
  subTitle?: string;
  allowMultiple?: boolean;
  accept?: string[];
  required?: boolean;
  error?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  id,
  onChange,
  title = 'Upload media or drag and drop',
  allowMultiple = false,
  accept = [],
  required = false,
  error,
}) => {
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const fileNames = Array.from(e.target.files)
        .map((file) => file.name)
        .join(', ');
      setUploadedFileName(fileNames);
      onChange(e);
    }
  };

  const acceptString = accept.join(',');
  const derivedSubtitle =
    accept.length > 0 ? accept.join(' · ').toUpperCase() : '';

  return (
    <div className="relative my-4">
      <input
        id={id}
        name={id}
        type="file"
        className="hidden"
        onChange={handleFileChange}
        multiple={allowMultiple}
        accept={acceptString}
        required={required}
      />
      <label
        htmlFor={id}
        className={`relative flex flex-col items-center gap-3 px-6 py-8 cursor-pointer transition-colors border border-dashed
          ${error
            ? 'border-[rgba(239,68,68,0.4)] hover:border-[#f87171] bg-[rgba(239,68,68,0.04)]'
            : uploadedFileName
              ? 'border-[rgba(146,222,113,0.4)] hover:border-accent bg-[rgba(146,222,113,0.04)]'
              : 'border-line hover:border-[rgba(255,255,255,0.25)] hover:bg-[rgba(255,255,255,0.02)]'
          }`}
      >
        {/* Icon */}
        <div className={`w-12 h-12 flex items-center justify-center ${uploadedFileName ? 'bg-[rgba(146,222,113,0.12)]' : 'bg-[rgba(255,255,255,0.04)]'}`}>
          {uploadedFileName
            ? <FileCheck className="w-5 h-5 text-accent" />
            : <Upload className="w-5 h-5 text-muted" />
          }
        </div>

        {/* Title */}
        <div className="text-center">
          <p className="font-['Archivo'] text-[13px] text-text">
            {title}
            {required && <span className="text-[#ef4444] ml-1">*</span>}
          </p>
          {derivedSubtitle && !uploadedFileName && (
            <p className="cg-label mt-1">{derivedSubtitle}</p>
          )}
        </div>

        {/* Uploaded file name */}
        {uploadedFileName && (
          <p className="font-['Space_Mono'] text-[10px] text-accent">
            ✓ {uploadedFileName}
          </p>
        )}

        {/* Error */}
        {error && (
          <p className="font-['Space_Mono'] text-[9px] text-[#f87171] uppercase tracking-[0.06em]">
            {error}
          </p>
        )}
      </label>
    </div>
  );
};

export default FileUpload;
